import { appPath, workAreaRoutePath } from '@featit/shared/addressing/paths';

export function projectItemsPath(projectSlug?: string | null): string {
  return projectSlug ? workAreaRoutePath(projectSlug, '/items') : appPath('/items');
}

export function projectHomePath(projectSlug?: string | null): string {
  return projectSlug ? workAreaRoutePath(projectSlug) : appPath('/home');
}

export function projectItemPath(
  projectSlug: string | null | undefined,
  itemKey: string | null | undefined,
  query = '',
): string {
  const basePath = projectItemsPath(projectSlug);
  const path = itemKey ? `${basePath}/${encodeURIComponent(itemKey)}` : basePath;
  return query ? `${path}?${query}` : path;
}

export function projectNewItemPath(projectSlug?: string | null): string {
  return `${projectItemsPath(projectSlug)}/new`;
}

export function projectRoadmapPath(projectSlug?: string | null): string {
  return projectSlug ? workAreaRoutePath(projectSlug, '/roadmap') : appPath('/roadmap');
}

export function projectActivityPath(projectSlug?: string | null): string {
  return projectSlug ? workAreaRoutePath(projectSlug, '/activity') : appPath('/activity');
}

export function projectViewsPath(projectSlug?: string | null): string {
  return projectSlug ? workAreaRoutePath(projectSlug, '/views') : appPath('/views');
}

export function projectViewPath(
  projectSlug: string | null | undefined,
  viewUrlId: string | null | undefined,
  query = '',
): string {
  const basePath = projectViewsPath(projectSlug);
  const path = viewUrlId ? `${basePath}/${encodeURIComponent(viewUrlId)}` : basePath;
  return query ? `${path}?${query}` : path;
}

export function projectSettingsPath(projectSlug?: string | null): string {
  return projectSlug ? workAreaRoutePath(projectSlug, '/settings') : appPath('/settings');
}

/**
 * F-0774: the address of a work area's settings in Settings — `?workArea=<ref>` and every other
 * parameter the caller gives (`section`, a notice's `request`/`answer`), an old `tab` read as
 * `section`. Without a work area, Settings itself.
 */
export function workAreaSettingsAddress(
  workAreaRef: string | null | undefined,
  query: string | URLSearchParams = '',
): string {
  const next = new URLSearchParams(query);
  const tab = next.get('tab');
  next.delete('tab');
  if (tab && !next.has('section')) next.set('section', tab);
  next.delete('workArea');
  // The work area first and readable (`workArea=acme/tracker`): a `/` needs no escape in a query.
  const parts = [
    ...(workAreaRef ? [`workArea=${encodeURIComponent(workAreaRef).replace(/%2F/gi, '/')}`] : []),
    ...(next.toString() ? [next.toString()] : []),
  ];
  return parts.length ? `${appPath('/settings')}?${parts.join('&')}` : appPath('/settings');
}

/**
 * "Decisions waiting for me" for one work area (F-0178).
 *
 * It has an unscoped twin, `/-/decisions`, which reads the selected work area — as `/-/items` and
 * `/-/roadmap` do (I-0449).
 */
export function projectDecisionsPath(projectSlug: string): string {
  return workAreaRoutePath(projectSlug, '/decisions');
}

/** Filing a decision (F-0178): scoped the same way, since a decision belongs to one work area. */
export function projectNewDecisionPath(projectSlug: string): string {
  return `${projectDecisionsPath(projectSlug)}/new`;
}

/** Revising an unanswered decision in place (I-0484): `/acme/tracker/-/decisions/<KEY>/revise`. */
export function projectReviseDecisionPath(projectSlug: string, decisionKey: string): string {
  return `${projectDecisionPath(projectSlug, decisionKey)}/revise`;
}

/**
 * One decision, selected **inside** the decisions surface rather than on the generic item route
 * (I-0474).
 *
 * The selection is in the address so that a link to a decision is shareable and the back button does
 * what it looks like it does; the list stays mounted around it. Opening one used to navigate to
 * the item's own address, which unmounted the list — with 38 open decisions on the board, that round
 * trip was the whole interaction.
 *
 * ⚠️ `decisions/new` must keep winning over this dynamic segment. React Router ranks a static segment
 * above a dynamic one, so `-/decisions/new` still files a decision rather than opening one named
 * "new"; `App.routes.test.tsx` pins it.
 */
export function projectDecisionPath(
  projectSlug: string | null | undefined,
  decisionKey: string | null | undefined,
): string {
  const basePath = projectSlug
    ? workAreaRoutePath(projectSlug, '/decisions')
    : appPath('/decisions');
  return decisionKey ? `${basePath}/${encodeURIComponent(decisionKey)}` : basePath;
}

/**
 * "Tasks for me" for one work area (I-0900), directly below "Decisions for me" in the sidebar.
 *
 * Unscoped like `/-/decisions`: `/-/tasks` reads the selected work area, as `/-/items` and
 * `/-/roadmap` do (I-0449).
 */
export function projectTasksPath(projectSlug?: string | null): string {
  return projectSlug ? workAreaRoutePath(projectSlug, '/tasks') : appPath('/tasks');
}

/**
 * One task's item open beside the Tasks list (I-0954), as a decision opens beside its list:
 * `/<work area>/-/tasks/<KEY>`, or `/-/tasks/<KEY>` unscoped.
 */
export function projectTaskPath(
  projectSlug: string | null | undefined,
  itemKey: string | null | undefined,
  questionId?: string | null,
): string {
  const basePath = projectTasksPath(projectSlug);
  const path = itemKey ? `${basePath}/${encodeURIComponent(itemKey)}` : basePath;
  return itemKey && questionId
    ? `${path}?${TASK_QUESTION_PARAM}=${encodeURIComponent(questionId)}`
    : path;
}

/** The question a task's address opens on, beside the Tasks list (`?question=<id>`). */
export const TASK_QUESTION_PARAM = 'question';
